'use client';

import { Button, buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getModelName } from '@/hooks/use-react-query-subscription/utils';
import { cn } from '@/lib/cn';
import { getConfig } from '@/lib/get-config';
import { Urls } from '@/lib/urls';
import {
  ArrowRightIcon,
  ChevronDoubleUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/16/solid';
import Link from 'next/link';
import { useState } from 'react';
import type {
  IntegrationUpgrade,
  IntegrationUpgradeGroup,
} from '../hooks/use-integration-upgrades';
import { useIntegrationUpgrades } from '../hooks/use-integration-upgrades';

function getSingleUpgradeUrl(upgrade: IntegrationUpgrade) {
  return Urls.settings.organization.integration(
    upgrade.manifest.application_id,
    upgrade.serviceKey,
    upgrade.agent.activationObjectId ?? undefined
  );
}

function getGroupUrl(group: IntegrationUpgradeGroup) {
  if (group.upgrades.length === 1) {
    return getSingleUpgradeUrl(group.upgrades[0]);
  }
  return Urls.settings.organization.integration(
    group.manifest.application_id,
    group.serviceKey
  );
}

function getVerboseName(contentType: string | undefined) {
  const modelName = contentType ? getModelName(contentType) : undefined;
  const verboseName = modelName ? getConfig(modelName)?.verboseName : undefined;
  return verboseName?.toLowerCase();
}

function GroupHeadline({ group }: { group: IntegrationUpgradeGroup }) {
  if (group.upgrades.length === 1) {
    const u = group.upgrades[0];
    return (
      <span className='block truncate text-sm font-medium'>
        Ny version tillgänglig för {u.servicePublicName} (
        {u.currentVersionLabel} → {u.latestVersionLabel})
      </span>
    );
  }
  const verboseName = getVerboseName(group.activationContentType);
  return (
    <span className='block truncate text-sm font-medium'>
      En ny version av {group.servicePublicName} är tillgänglig för{' '}
      {group.upgrades.length} {verboseName ? verboseName : 'objekt'} (ny
      version: {group.latestVersionLabel})
    </span>
  );
}

function GroupRow({ group }: { group: IntegrationUpgradeGroup }) {
  const isMulti = group.upgrades.length > 1;
  const verboseName = getVerboseName(group.activationContentType);
  return (
    <li className='flex flex-wrap items-center justify-between gap-3 py-2'>
      <div className='min-w-0'>
        <p className='truncate text-sm font-medium'>
          {group.servicePublicName}
          {isMulti && (
            <span className='ml-2 text-muted-foreground'>
              · {group.upgrades.length} {verboseName}
            </span>
          )}
        </p>
        <p className='text-xs text-muted-foreground'>
          {isMulti
            ? `Ny version: ${group.latestVersionLabel}`
            : `${group.upgrades[0].currentVersionLabel} → ${group.latestVersionLabel}`}
        </p>
      </div>
      <Link
        prefetch={false}
        href={getGroupUrl(group)}
        className={cn(
          buttonVariants({ variant: 'default', size: 'sm' }),
          'flex items-center gap-2'
        )}
      >
        <span>Hantera</span>
        <ArrowRightIcon className='size-4' />
      </Link>
    </li>
  );
}

export function IntegrationUpgradesBanner() {
  const { groups, isLoading } = useIntegrationUpgrades();
  const [isExpanded, setIsExpanded] = useState(false);
  if (isLoading || groups.length === 0) return null;

  const isSingleGroup = groups.length === 1;
  const onlyGroup = groups[0];
  const isSingleAgent = isSingleGroup && onlyGroup.upgrades.length === 1;

  return (
    <Card className='mb-6 gap-3 px-4 py-3'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted'>
            <ChevronDoubleUpIcon className='size-5 text-foreground' />
          </div>
          <div className='min-w-0 flex-1'>
            {isSingleGroup ? (
              <GroupHeadline group={onlyGroup} />
            ) : (
              <span className='block truncate text-sm font-medium'>
                {groups.length} integrationer har nya versioner tillgängliga
              </span>
            )}
          </div>
        </div>
        {isSingleAgent ? (
          <Link
            prefetch={false}
            href={getSingleUpgradeUrl(onlyGroup.upgrades[0])}
            className={cn(
              buttonVariants({ variant: 'default', size: 'sm' }),
              'flex items-center gap-2'
            )}
          >
            <span>Hantera</span>
            <ArrowRightIcon className='size-4' />
          </Link>
        ) : isSingleGroup ? (
          <Link
            prefetch={false}
            href={getGroupUrl(onlyGroup)}
            className={cn(
              buttonVariants({ variant: 'default', size: 'sm' }),
              'flex items-center gap-2'
            )}
          >
            <span>Hantera</span>
            <ArrowRightIcon className='size-4' />
          </Link>
        ) : (
          <Button
            variant='outline'
            size='sm'
            onClick={() => setIsExpanded((p) => !p)}
            className='flex items-center gap-2'
            aria-expanded={isExpanded}
            aria-controls='integration-upgrades-list'
          >
            <span>{isExpanded ? 'Dölj' : 'Visa'} integrationer</span>
            <ChevronDownIcon
              className={cn(
                'size-4 transition-transform',
                isExpanded && 'rotate-180'
              )}
            />
          </Button>
        )}
      </div>

      {!isSingleGroup && isExpanded && (
        <ul
          id='integration-upgrades-list'
          className='divide-y divide-border border-t pt-2'
        >
          {groups.map((group) => (
            <GroupRow
              key={`${group.manifest.application_id}-${group.serviceKey}`}
              group={group}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}
